import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { getStatusDisplay, exportToCSV, exportToJSON } from '../utils/activationCodeUtils';
import TargetTitle from './TargetTitle';
import QRCode from 'react-qr-code';

const ActivationCodeList = ({ 
  codes = [], 
  loading = false, 
  onRefresh, 
  onRevoke, 
  onExport,
  pagination = null,
  onPageChange
}) => {
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [showQRDialog, setShowQRDialog] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null); // { ids, label } khi đang chờ xác nhận thu hồi
  const [filters, setFilters] = useState({
    q: '',
    status: '',
    sku: ''
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCodes(codes.map(code => code._id || code.id));
    } else {
      setSelectedCodes([]);
    }
  };

  const handleSelectCode = (codeId, checked) => {
    if (checked) {
      setSelectedCodes(prev => [...prev, codeId]);
    } else {
      setSelectedCodes(prev => prev.filter(id => id !== codeId));
    }
  };

  const handleBulkRevoke = () => {
    if (selectedCodes.length === 0) return;
    setRevokeTarget({ ids: selectedCodes, label: `${selectedCodes.length} mã đã chọn` });
  };

  // Thu hồi không hoàn tác được, nên luôn hỏi lại qua modal
  const handleConfirmRevoke = () => {
    const { ids } = revokeTarget;
    onRevoke?.(ids);
    setSelectedCodes(prev => prev.filter(id => !ids.includes(id)));
    setRevokeTarget(null);
  };

  const handleExport = (format) => {
    const selectedData = selectedCodes.length > 0 
      ? codes.filter(code => selectedCodes.includes(code._id || code.id))
      : codes;

    if (format === 'csv') {
      exportToCSV(selectedData);
    } else if (format === 'json') {
      exportToJSON(selectedData);
    } else if (onExport) {
      onExport(format, selectedData);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = (expiryDate - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0; // Expires within 30 days
  };

  // Get the unique ID for a code (handle both MongoDB _id and regular id)
  const getCodeId = (code) => code._id || code.id;

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header với filters và actions */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Danh sách mã kích hoạt</h3>
          <div className="flex items-center space-x-2">
            {selectedCodes.length > 0 && (
              <>
                <span className="text-sm text-gray-600">
                  Đã chọn {selectedCodes.length} mã
                </span>
                <button
                  onClick={handleBulkRevoke}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Thu hồi
                </button>
              </>
            )}
            
            {/* Export buttons */}
            <div className="relative group">
              <button className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                📥 Xuất mã
              </button>
              <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={() => handleExport('csv')}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  📊 CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <button
                    onClick={() => handleExport('pdf')}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    📄 PDF
                  </button>
                </button>
              </div>
            </div>
            
            <button
              onClick={onRefresh}
              disabled={loading}
              className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              🔄 Tải lại
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Input
              placeholder="Tìm kiếm mã, sách..."
              value={filters.q}
              onChange={(e) => setFilters(prev => ({ ...prev, q: e.target.value }))}
            />
          </div>
          
          
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={codes.length > 0 && selectedCodes.length === codes.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mã kích hoạt
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sách
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lô mã
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trạng thái
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày tạo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hết hạn
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sử dụng
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="9" className="px-4 py-8 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                    Đang tải...
                  </div>
                </td>
              </tr>
            ) : codes.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                  <div className="text-4xl mb-2">📭</div>
                  Không có mã kích hoạt nào
                </td>
              </tr>
            ) : (
              codes.map((code) => {
                const statusDisplay = getStatusDisplay(code.status);
                const expiringSoon = isExpiringSoon(code.expires_at);
                const codeId = getCodeId(code);
                
                return (
                  <tr key={codeId} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedCodes.includes(codeId)}
                        onChange={(e) => handleSelectCode(codeId, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                          {code.code}
                        </code>
                        {code.qr_code && (
                          <button
                            onClick={() => setShowQRDialog(code)}
                            className="ml-2 text-blue-600 hover:text-blue-700"
                            title="Xem QR Code"
                          >
                            📱
                          </button>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        <TargetTitle item={code} />
                      </div>
                    </td>
                    
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{code.batch_name || 'N/A'}</div>
                    </td>
                    
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDisplay.badgeClass}`}>
                        {statusDisplay.icon} {statusDisplay.label}
                      </span>
                    </td>
                    
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatDate(code.created_at)}
                    </td>
                    
                    <td className="px-4 py-4 text-sm">
                      <div className={expiringSoon ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                        {formatDate(code.expires_at)}
                        {expiringSoon && <div className="text-xs">⚠️ Sắp hết hạn</div>}
                      </div>
                    </td>
                    
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {code.used_at ? (
                        <div>
                          <div>{formatDate(code.used_at)}</div>
                          {code.used_by && (
                            <div className="text-xs">bởi {code.used_by}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Chưa sử dụng</span>
                      )}
                    </td>
                    
                    <td className="px-4 py-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowQRDialog(code)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Xem chi tiết"
                        >
                          👁️
                        </button>
                        {code.status === 'unused' && (
                          <button
                            onClick={() => setRevokeTarget({ ids: [codeId], label: `mã ${code.code}` })}
                            className="text-red-600 hover:text-red-700"
                            title="Thu hồi mã"
                          >
                            🚫
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.last_page > 1 && (
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Hiển thị {pagination.from || 1} đến {pagination.to || pagination.total} trong tổng số {pagination.total} mã
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Trước
              </button>
              
              {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                const page = Math.max(1, Math.min(pagination.last_page - 4, pagination.current_page - 2)) + i;
                if (page > pagination.last_page) return null;
                
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      page === pagination.current_page
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => onPageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sau
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Render có điều kiện để lúc đóng không chạy hiệu ứng mờ với nội dung đã bị xóa */}
      {revokeTarget && (
        <Dialog open onOpenChange={(open) => !open && setRevokeTarget(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Thu hồi mã kích hoạt</DialogTitle>
              <DialogDescription>
                Bạn có chắc chắn muốn thu hồi {revokeTarget.label}? Mã đã thu hồi sẽ không dùng được nữa và không thể khôi phục.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRevokeTarget(null)}>Hủy</Button>
              <Button variant="danger" onClick={handleConfirmRevoke}>Thu hồi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* QR Code Dialog */}
      {showQRDialog && (
        <Dialog open={!!showQRDialog} onOpenChange={() => setShowQRDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>QR Code - {showQRDialog.code}</DialogTitle>
            </DialogHeader>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <QRCode
                  value={showQRDialog.code}
                  size={200}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Mã:</strong> {showQRDialog.code}</p>
                <p><strong>SKU:</strong> {showQRDialog.sku}</p>
                <p><strong>Sách:</strong> <TargetTitle item={showQRDialog} /></p>
                <p><strong>Lô mã:</strong> {showQRDialog.batch_name || 'N/A'}</p>
                <p><strong>Trạng thái:</strong> {getStatusDisplay(showQRDialog.status).label}</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ActivationCodeList;
